/**
 * Zugriff auf die Portal-API.
 *
 * Eigener Speicherschlüssel und eigene Endpunkte – ein Portaltoken und ein
 * internes Token dürfen sich nie vermischen. Auch hier `sessionStorage`:
 * Fenster zu, Anmeldung weg. Kunden nutzen das Portal oft auf geteilten
 * Geräten.
 */

const SCHLUESSEL = 'tb-portal-sitzung';

export interface Angemeldet {
  email: string;
  name: string | null;
}

export function tokenLesen(): string | null {
  return sessionStorage.getItem(SCHLUESSEL);
}

export function tokenSetzen(token: string | null): void {
  if (token) sessionStorage.setItem(SCHLUESSEL, token);
  else sessionStorage.removeItem(SCHLUESSEL);
}

/** Rückrufe, die bei einer ungültigen Sitzung ausgelöst werden. */
const beiSitzungsverlust: (() => void)[] = [];

export function aufSitzungsverlust(melden: () => void): void {
  beiSitzungsverlust.push(melden);
}

export class ApiFehler extends Error {
  constructor(
    override readonly message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function anfrage<T>(
  pfad: string,
  optionen: { methode?: string; koerper?: unknown } = {},
): Promise<T> {
  const token = tokenLesen();
  const antwort = await fetch(pfad, {
    method: optionen.methode ?? 'GET',
    headers: {
      ...(optionen.koerper !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: optionen.koerper !== undefined ? JSON.stringify(optionen.koerper) : undefined,
  });

  if (antwort.status === 401) {
    // Abgelaufene oder ungültige Sitzung: Token wegwerfen UND die Oberfläche
    // benachrichtigen. Ohne die Benachrichtigung bliebe die Anwendung stehen
    // und zeigte in jeder Liste „Sitzung ungültig" — der Benutzer sähe nicht,
    // dass er sich schlicht neu anmelden muss.
    tokenSetzen(null);
    for (const melden of beiSitzungsverlust) melden();
  }

  if (!antwort.ok) {
    let text = `Fehler ${antwort.status}`;
    try {
      const daten = (await antwort.json()) as { error?: string };
      if (daten.error) text = daten.error;
    } catch {
      /* keine JSON-Antwort – Standardtext bleibt */
    }
    throw new ApiFehler(text, antwort.status);
  }

  return (await antwort.json()) as T;
}

export const api = {
  get: <T>(pfad: string) => anfrage<T>(pfad),
  post: <T>(pfad: string, koerper?: unknown) => anfrage<T>(pfad, { methode: 'POST', koerper }),
  patch: <T>(pfad: string, koerper: unknown) => anfrage<T>(pfad, { methode: 'PATCH', koerper }),
  del: <T>(pfad: string) => anfrage<T>(pfad, { methode: 'DELETE' }),
};

/** Baut `?a=1&b=2`, lässt leere Werte weg. */
export function abfrage(werte: Record<string, string | number | undefined | null>): string {
  const parameter = new URLSearchParams();
  for (const [name, wert] of Object.entries(werte)) {
    if (wert !== undefined && wert !== null && `${wert}` !== '') parameter.set(name, `${wert}`);
  }
  const text = parameter.toString();
  return text ? `?${text}` : '';
}
